import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { HomeProps } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { loadGoods, loadTracks, loadWorks } from '@/lib/content'
import { getStreamFeed } from '@/lib/stream-feed'
import SmoothScroll from '@/components/landing/smooth-scroll'
import SiteHeader from '@/components/landing/site-header'
import SiteFooter from '@/components/landing/site-footer'
import StarField from '@/components/landing/decor/star-field'
import HeroSection from '@/components/landing/hero-section'
import ChaptersSection from '@/components/landing/chapters-section'
import PlantInterlude from '@/components/landing/plant-interlude'
import StreamSection from '@/components/landing/stream-section'
import MusicSection from '@/components/landing/music-section'
import GoodsSection from '@/components/landing/goods-section'
import WorksSection from '@/components/landing/works-section'
import ContactSection from '@/components/landing/contact-section'
import LiveWidget from '@/components/landing/live-widget'

// Defaults cover a page that was hot-swapped in the browser while still holding the props of an
// earlier version; a real request always receives every value.
const Home: FC<Partial<HomeProps>> = ({
    streams = [],
    spotlight = null,
    tracks = [],
    goods = [],
    works = [],
    ogImage = null
}): ReactNode => {
    return (
        <SmoothScroll>
            <Layout header={<SiteHeader />} footer={<SiteFooter />}>
                <Seo ogImage={ogImage ?? undefined} />

                <StarField />
                <HeroSection />
                <ChaptersSection />
                <PlantInterlude />
                <StreamSection streams={streams} />
                <MusicSection tracks={tracks} />
                <GoodsSection goods={goods} />
                <WorksSection works={works} />
                <ContactSection />
                <LiveWidget initial={spotlight} />
            </Layout>
        </SmoothScroll>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [feed, tracks, goods, works] = await Promise.all([getStreamFeed(), loadTracks(), loadGoods(), loadWorks()])

    return {
        props: {
            streams: feed.streams,
            spotlight: feed.spotlight,
            tracks,
            goods,
            works,
            ogImage: null
        },
        revalidate: 60
    }
}

export default Home
